import React, { useState, useEffect, useCallback } from 'react';
import { WidgetProps } from '@rjsf/utils';
import { Autocomplete, TextField } from '@mui/material';
import { INPUT_STYLE } from '../../../../../utils/styleHelpers';
import { useJsApiLoader } from '@react-google-maps/api';
import debounce from 'lodash.debounce';
import { DetailRow } from '../../../DetailsDrawer/DetailRow';
import { getConfiguration } from '../../../../../store/Configuration';
import { withGoogleMapsApiKey } from '../../../../../utils/withGoogleMapsApiKey';

const ProductAddressAutocomplete = (props: WidgetProps) => {
  const { value, onChange, disabled } = props;
  const [userInput, setUserInput] = useState(value || '');
  const [dirty, setDirty] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(value || '');
  const [geocodedAddresses, setGeocodedAddresses] = useState<string[]>([]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: getConfiguration().REACT_APP_GOOGLE_MAPS_API_KEY
  });

  const handleGeocode = useCallback(
    debounce(async (address: string) => {
      if (!address) return setGeocodedAddresses([]);
      /**
       * @documentation https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data?db=gems#AutocompleteRequest
       * @example https://developers.google.com/maps/documentation/javascript/examples/place-autocomplete-data-session
       */
      const places = await google.maps.importLibrary('places');
      // @ts-expect-error type
      const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: address
      });
      const geocoder = new window.google.maps.Geocoder();
      // @ts-expect-error type
      const geocodingPromises = suggestions.map((sugg) => {
        return new Promise((resolve) => {
          const addressToGeocode = sugg.placePrediction.text.toString();
          geocoder.geocode({ address: addressToGeocode }, (results, _status) => {
            const newDetails = {
              formatted_address: results ? results[0]?.formatted_address : addressToGeocode
            };
            resolve(newDetails);
          });
        });
      });
      const detailedSuggestions = await Promise.all(geocodingPromises);
      // @ts-expect-error type
      setGeocodedAddresses(detailedSuggestions.map((sugg) => sugg.formatted_address));
    }, 250),
    []
  );

  useEffect(() => {
    if (isLoaded && process.env.NODE_ENV !== 'test') handleGeocode(userInput);
  }, [userInput]);

  const handleChange = (_e: React.SyntheticEvent<Element, Event>, value: string | null) => {
    if (!dirty) setDirty(true);
    setUserInput(value || '');
    onChange(value || '');
  };

  const handleInputChange = (_e: React.SyntheticEvent<Element, Event>, value: string | null) => {
    if (!dirty) setDirty(true);
    setUserInput(value || '');
    setSelectedAddress(value || '');
    onChange(value || '');
  };

  const getHelperText = () => {
    if (!isErrorState()) return undefined;
    if (props.rawErrors && props.rawErrors.length > 0) return props.rawErrors[0];
    return 'Address is required';
  };

  const isErrorState = () => {
    if (disabled) return false;
    return dirty && ((props.rawErrors && props.rawErrors.length > 0) || (props.required && selectedAddress === ''));
  };

  return (
    <Autocomplete
      disabled={disabled}
      value={selectedAddress || userInput}
      inputValue={userInput}
      onInputChange={handleInputChange}
      ListboxProps={{ style: { maxHeight: 150 } }}
      onChange={handleChange}
      onBlur={() => setDirty(true)}
      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
      options={geocodedAddresses}
      getOptionLabel={(option: string) => option}
      freeSolo={true}
      filterOptions={(options) => options}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.uiSchema?.['ui:placeholder']}
          error={isErrorState()}
          helperText={getHelperText()}
          required={props.required}
          InputProps={{
            ...params.InputProps,
            style: { fontSize: '.875rem' },
            name: props.uiSchema?.['ui:placeholder']
          }}
          className={INPUT_STYLE}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          className="text-sm14 py-1 pl-4 cursor-pointer hover:bg-grey-1">
          {option}
        </li>
      )}
    />
  );
};

const GatedProductAddressAutocomplete = withGoogleMapsApiKey(ProductAddressAutocomplete);

// Readonly rendering needs no Maps loader, so it stays outside the API-key gate.
export const ProductAddressInput = (props: WidgetProps) => {
  if (props.readonly) {
    if (props.formContext?.hideEmpty && !props.value) return null;

    return (
      <div>
        <DetailRow
          key={props.id}
          colId={props.id}
          title={props.uiSchema?.['ui:placeholder'] || ''}
          value={props.value || ''}
        />
      </div>
    );
  }

  return <GatedProductAddressAutocomplete {...props} />;
};

export default ProductAddressInput;
